import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../../contexts/StoreContext';
import { PageContext } from '../../contexts/PageContext';
import SmartModal from './SmartModal';

/**
 * SmartModalContainer - контейнер для глобальных модалок
 * Подключается к UIStore и PageContext для отображения модалок
 */
const SmartModalContainer = observer(() => {
    const rootStore = useContext(StoreContext);
    const pageContext = useContext(PageContext);
    const { ui, auth, models } = rootStore;
    
    // Если нет активной модалки - ничего не рендерим
    if (!ui.currentModal) {
        return null;
    }
    
    const modal = ui.currentModal;
    
    // Определяем entity из модалки или из контекста страницы
    const entity = modal.entityName || pageContext?.modelName;
    
    console.log('[SmartModalContainer] Debug:', {
        modalEntityName: modal.entityName,
        pageContextModelName: pageContext?.modelName,
        entity,
        availableSchemas: Object.keys(models.schemas || {})
    });
    
    // Получаем схему ВСЕГДА из models по entity (единый источник истины)
    const schema = entity ? models.schemas[entity] : null;
    
    // Получаем store с правильным приоритетом
    let store = null;

    // Приоритет 1: store переданный напрямую через modal (для локальных коллекций)
    if (modal.store) {
        store = modal.store;
        console.log('[SmartModalContainer] Using modal.store for', entity);
    }
    // Приоритет 2: используем store из контекста только если entity совпадает с modelName
    else if (pageContext?.store && pageContext?.modelName === entity) {
        store = pageContext.store;
        console.log('[SmartModalContainer] Using pageContext.store for', entity);
    }
    // Приоритет 3: универсальный поиск (локальная или глобальная коллекция)
    else if (entity) {
        store = (models.getCollection ? models.getCollection(entity) : null) ||
                models.getCollectionManager(entity);
        console.log('[SmartModalContainer] Using getCollection/getCollectionManager for', entity);
    }
    
    // Обработчик сохранения - делегируем обработку в store
    const handleSave = async (data) => {
        if (!store) {
            console.error('[SmartModalContainer] No store in context');
            return false;
        }
        
        try {
            let success = false;
            
            // Store сам обработает наследование и подготовку данных через MST actions
            switch (modal.operation) {
                case 'create':
                    // Store.create уже вызывает prepareCreateData внутри
                    success = await store.create(data);
                    break;
                    
                case 'edit':
                case 'update':
                    // Передаем данные как есть, store.save вызовет prepareUpdateData
                    const itemToUpdate = modal.data?.id ?
                        { ...modal.data, ...data } :
                        { id: modal.targetId, ...data };
                    success = await store.save(itemToUpdate);
                    break;
                    
                case 'delete':
                    const targetId = modal.targetId || modal.data?.id;
                    if (targetId) {
                        success = await store.delete(targetId);
                    }
                    break;
                    
                default:
                    console.warn(`[SmartModalContainer] Unknown operation: ${modal.operation}`);
            }
            
            if (success) {
                ui.closeModal(modal.id);
            }
            
            return success;
        } catch (error) {
            console.error('[SmartModalContainer] Operation error:', error);
            throw error;
        }
    };
    
    // Обработчик закрытия с защитой от повторного вызова
    const handleClose = () => {
        // Проверяем что модалка еще существует в дереве
        if (ui.currentModal && ui.currentModal.id === modal.id) {
            ui.closeModal(modal.id);
        }
    };
    
    // Для модалки удаления показываем подтверждение
    if (modal.type === 'delete' || modal.operation === 'delete') {
        const itemName = modal.data?.name || modal.data?.title || modal.data?.id || 'элемент';
        
        return (
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal modal-confirm" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>{modal.title || 'Подтверждение удаления'}</h3>
                        <button className="btn-close" onClick={handleClose}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <p>{modal.message || `Вы уверены, что хотите удалить ${itemName}?`}</p>
                    </div>
                    <div className="modal-footer">
                        <button 
                            className="btn btn-secondary" 
                            onClick={handleClose}
                        >
                            Отмена
                        </button>
                        <button 
                            className="btn btn-danger" 
                            onClick={() => handleSave(modal.data)}
                        >
                            Удалить
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Для информационных модалок
    if (modal.type === 'info' || modal.type === 'confirm') {
        return (
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal modal-info" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>{modal.title || 'Информация'}</h3>
                        <button className="btn-close" onClick={handleClose}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <p>{modal.message || ''}</p>
                    </div>
                    <div className="modal-footer">
                        {modal.type === 'confirm' ? (
                            <>
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleClose}
                                >
                                    Отмена
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={() => {
                                        if (modal.onConfirm) modal.onConfirm();
                                        ui.closeModal(modal.id);
                                    }}
                                >
                                    Подтвердить
                                </button>
                            </>
                        ) : (
                            <button 
                                className="btn btn-primary" 
                                onClick={handleClose}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    // Получаем родительский контекст для наследования
    // Используем переданный контекст или берем из modal.parentContext
    const parentContext = modal.parentContext || null;

    console.log('[SmartModalContainer] Modal object:', modal);
    console.log('[SmartModalContainer] ParentContext:', parentContext);
    
    // Для CRUD операций используем SmartModal
    return (
        <SmartModal
            isOpen={true}
            onClose={handleClose}
            onSave={handleSave}
            item={modal.data}
            schema={schema}
            modelName={entity}
            isCreating={modal.operation === 'create'}
            modalTitle={modal.title}
            models={rootStore.models}  // Передаем models store для совместимости
            parentContext={parentContext}
        />
    );
});

export default SmartModalContainer;